import { useEffect, useState, useCallback } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Languages, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANG_NAMES = {
  en: 'Engleski', de: 'Njemački', es: 'Španjolski', fr: 'Francuski',
  pt: 'Portugalski', zh: 'Kineski', ar: 'Arapski', hi: 'Hindski',
  ru: 'Ruski', ja: 'Japanski',
  sl: 'Slovenski', sk: 'Slovački', hu: 'Mađarski',
};

export default function AdminI18nCoverage() {
  const { authFetch } = useAdmin();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const r = await authFetch(`${API}/admin/i18n/coverage`);
    if (r.ok) setData(await r.json());
    setLoading(false);
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const translateAll = async (lang) => {
    setBusy(lang);
    const r = await authFetch(`${API}/admin/i18n/translate-all`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lang }),
    });
    if (r.ok) {
      const d = await r.json();
      toast.success(`Zakazano ${d.scheduled} članaka za ${LANG_NAMES[lang]}`);
      setTimeout(load, 2000);
    }
    setBusy('');
  };

  if (loading || !data) return <div className="p-10"><Loader2 className="animate-spin text-zinc-500" /></div>;

  const heatColor = (pct) => {
    if (pct >= 95) return 'bg-emerald-600';
    if (pct >= 70) return 'bg-lime-500';
    if (pct >= 40) return 'bg-amber-500';
    if (pct >= 15) return 'bg-orange-500';
    return 'bg-red-600';
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl" data-testid="admin-i18n-coverage">
      <div className="flex items-center gap-3 mb-5">
        <Languages className="text-red-500" size={24} />
        <div>
          <h1 className="text-2xl font-bold">Pokrivenost prijevoda</h1>
          <p className="text-sm text-zinc-400">Postotak članaka s postojećim prijevodom po jeziku.</p>
        </div>
      </div>

      <p className="text-xs text-zinc-400 mb-3">
        Ukupno objavljenih članaka: <b className="text-white">{data.total_articles}</b>
      </p>

      <div className="space-y-2">
        {data.languages.map(row => (
          <div key={row.lang} className="p-3 rounded-lg border border-zinc-800 bg-zinc-900/50" data-testid={`i18n-row-${row.lang}`}>
            <div className="flex items-center justify-between mb-1.5">
              <div>
                <span className="text-sm font-bold mr-2 uppercase">{row.lang}</span>
                <span className="text-xs text-zinc-400">{LANG_NAMES[row.lang] || row.lang}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-zinc-400">{row.covered}/{data.total_articles}</span>
                <span className="text-sm font-bold">{row.pct}%</span>
                <button onClick={() => translateAll(row.lang)} disabled={busy === row.lang || row.pct >= 100}
                        data-testid={`translate-${row.lang}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-zinc-800 hover:bg-zinc-700 rounded text-xs disabled:opacity-40">
                  {busy === row.lang ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                  Prevedi {row.missing}
                </button>
              </div>
            </div>
            <div className="h-3 bg-zinc-800 rounded overflow-hidden">
              <div className={`h-3 ${heatColor(row.pct)} transition-all`} style={{ width: `${row.pct}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
